'use client'
import { useEffect, useState } from 'react'
import { Download } from 'lucide-react'
import { Tooltip } from 'react-tooltip'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

interface NavigatorStandalone extends Navigator {
  standalone?: boolean
}

// Check if running in an iframe
const isInIframe = () => {
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

// Detect platform
const detectPlatform = () => {
  const userAgent = window.navigator.userAgent.toLowerCase()
  const platform = window.navigator.platform.toLowerCase()

  return {
    isIOS: /iphone|ipad|ipod/.test(userAgent),
    isAndroid: /android/.test(userAgent),
    isWindows: /win/.test(platform),
    isMac: /mac/.test(platform),
    isLinux: /linux/.test(platform) && !/android/.test(userAgent),
    isMobile: /iphone|ipad|ipod|android/.test(userAgent),
    isDesktop: !/iphone|ipad|ipod|android/.test(userAgent),
  }
}

// Check if PWA is already installed
const isInstalled = (): boolean => {
  // Check standalone mode
  const standalone =
    (window.navigator as NavigatorStandalone).standalone ||
    window.matchMedia('(display-mode: standalone)').matches

  // Check localStorage for installation tracking
  const wasInstalled = localStorage.getItem('pwa-installed') === 'true'

  return standalone || wasInstalled
}

// Check if browser supports PWA installation
const supportsPWAInstall = (): boolean => {
  const { isIOS, isAndroid, isWindows, isMac, isLinux, isDesktop } =
    detectPlatform()

  // iOS always supports (manual install)
  if (isIOS) return true

  // Android supports via beforeinstallprompt
  if (isAndroid) return true

  // Desktop browsers that support PWA
  if (isDesktop) {
    // Chrome, Edge, Opera on Windows/Mac/Linux
    const userAgent = window.navigator.userAgent.toLowerCase()
    const isChrome = /chrome/.test(userAgent) && !/edg/.test(userAgent)
    const isEdge = /edg/.test(userAgent)
    const isOpera = /opr/.test(userAgent)
    const isFirefox = /firefox/.test(userAgent)
    const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent)

    // Chrome, Edge, Opera support PWA on all desktop platforms
    if (isChrome || isEdge || isOpera) return true

    // Firefox supports PWA on Windows/Mac/Linux
    if (isFirefox && (isWindows || isMac || isLinux)) return true

    // Safari supports PWA on Mac
    if (isSafari && isMac) return true
  }

  return false
}

export default function InstallPWAButton() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [isInStandalone, setIsInStandalone] = useState(false)
  const [isInFrame, setIsInFrame] = useState(false)

  useEffect(() => {
    // Detect if in iframe
    const inFrame = isInIframe()
    setIsInFrame(inFrame)

    // Detect platform
    const detectedPlatform = detectPlatform()
    setIsIOS(detectedPlatform.isIOS)

    // Check if already installed
    const installed = isInstalled()
    setIsInStandalone(installed)

    // If already installed, don't show button
    if (installed) {
      setIsVisible(false)
      return
    }

    // Check if browser supports PWA
    if (!supportsPWAInstall()) {
      setIsVisible(false)
      return
    }

    // For iOS, show button if not installed
    if (detectedPlatform.isIOS && !installed) {
      setIsVisible(true)
      return
    }

    let timeoutId: NodeJS.Timeout | null = null
    let hasPromptFired = false

    // Android/Desktop: listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      hasPromptFired = true
      const promptEvent = e as BeforeInstallPromptEvent
      setDeferredPrompt(promptEvent)
      setIsVisible(true)

      // Clear timeout if prompt fired
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    // Listen for app installation to update state
    const handleAppInstalled = () => {
      localStorage.setItem('pwa-installed', 'true')
      setIsInStandalone(true)
      setIsVisible(false)
      setDeferredPrompt(null)
    }

    window.addEventListener('appinstalled', handleAppInstalled)

    // If in iframe and beforeinstallprompt doesn't fire, still show button for supported browsers
    // This handles WordPress iframe embedding - the event may not fire in iframes
    if (inFrame) {
      // Check after a delay to see if event fired
      timeoutId = setTimeout(() => {
        if (!hasPromptFired && supportsPWAInstall() && !installed) {
          // Show button anyway for supported browsers in iframe
          // User can still install via browser menu or we can open in new window
          setIsVisible(true)
        }
      }, 1500)
    } else {
      // For non-iframe contexts, wait longer for beforeinstallprompt
      // Only show button if we have the prompt or after a longer delay
      if (detectedPlatform.isDesktop && !installed) {
        // Wait longer for desktop browsers - some delay the prompt
        timeoutId = setTimeout(() => {
          if (!hasPromptFired && supportsPWAInstall()) {
            setIsVisible(true)
          }
        }, 3000) // Increased delay to 3 seconds
      } else if (detectedPlatform.isAndroid && !installed) {
        // For Android, wait a bit but not too long
        timeoutId = setTimeout(() => {
          if (!hasPromptFired && supportsPWAInstall()) {
            setIsVisible(true)
          }
        }, 2500)
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      )
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  // Get browser-specific installation instructions
  const getInstallInstructions = () => {
    const userAgent = window.navigator.userAgent.toLowerCase()
    const platform = detectPlatform()

    if (platform.isWindows) {
      if (/edg/.test(userAgent)) {
        return 'Click the install icon (➕) in the address bar, or go to Menu (⋯) > Apps > Install this site as an app'
      } else if (/chrome/.test(userAgent)) {
        return 'Click the install icon (➕) in the address bar, or go to Menu (⋮) > Install ProInstaller AI'
      } else if (/firefox/.test(userAgent)) {
        return 'Go to Menu (☰) > Install Site as App, or look for the install icon in the address bar'
      }
    } else if (platform.isMac) {
      if (/safari/.test(userAgent) && !/chrome/.test(userAgent)) {
        return 'Go to File > Add to Dock, or use Share button > Add to Home Screen'
      } else if (/chrome/.test(userAgent)) {
        return 'Click the install icon (➕) in the address bar, or go to Menu (⋮) > Install ProInstaller AI'
      } else if (/edg/.test(userAgent)) {
        return 'Click the install icon (➕) in the address bar, or go to Menu (⋯) > Apps > Install this site as an app'
      }
    } else if (platform.isLinux) {
      if (/chrome/.test(userAgent)) {
        return 'Click the install icon (➕) in the address bar, or go to Menu (⋮) > Install ProInstaller AI'
      } else if (/firefox/.test(userAgent)) {
        return 'Go to Menu (☰) > Install Site as App'
      }
    } else if (platform.isAndroid) {
      return 'Look for "Add to Home Screen" or "Install" in the browser menu (⋮) or address bar'
    }

    return 'Look for "Install" or "Add to Home Screen" in your browser menu or address bar'
  }

  const handleInstallClick = async () => {
    // iOS: Show instructions (handled by tooltip, button is just for UI)
    if (isIOS && !isInStandalone) {
      // iOS doesn't support programmatic install
      // Tooltip already shows instructions
      return
    }

    // Android/Desktop: Use deferred prompt if available
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt()
        const choiceResult = await deferredPrompt.userChoice

        if (choiceResult.outcome === 'accepted') {
          // Mark as installed in localStorage
          localStorage.setItem('pwa-installed', 'true')
          setIsInStandalone(true)
          setIsVisible(false)
        }

        setDeferredPrompt(null)
      } catch {
        // If prompt fails and we're in iframe, try opening in new window
        if (isInFrame) {
          const currentUrl = window.location.href
          window.open(currentUrl, '_blank')
        } else {
          // Show instructions if prompt fails
          const instructions = getInstallInstructions()
          alert(`Install prompt failed. ${instructions}`)
        }
      }
    } else if (isInFrame) {
      // If in iframe and no prompt available, open in new window
      // This helps with WordPress iframe embedding - PWA install works better in top-level window
      const currentUrl = window.location.href
      const newWindow = window.open(currentUrl, '_blank')

      // If popup blocked, show message
      if (!newWindow) {
        const instructions = getInstallInstructions()
        alert(
          `Popup blocked. Please allow popups, or open this page in a new tab.\n\nAlternatively: ${instructions}`
        )
      }
    } else {
      // No deferred prompt but button is visible - show instructions
      const instructions = getInstallInstructions()
      alert(
        `To install this app:\n\n${instructions}\n\nOr wait a moment and try clicking the install button again.`
      )
    }
  }

  // Don't show if already installed
  if (isInStandalone) {
    return null
  }

  // iOS fallback with instructions
  if (isIOS && !isInStandalone) {
    return (
      <div className='flex items-center gap-2 rounded-lg'>
        <button
          onClick={handleInstallClick}
          className='cursor-pointer flex items-center justify-center'
          aria-label='Install app'
        >
          <span
            data-tooltip-id='installPwaTooltip'
            data-tooltip-content='To install, tap the share icon and then "Add to Home Screen"'
            className='items-center relative block'
          >
            <Download
              className='h-auto w-full max-w-[20px] md:max-w-[24px] block transition-opacity hover:opacity-80'
              aria-hidden='true'
            />
          </span>
        </button>
        <Tooltip
          id='installPwaTooltip'
          content='To install, tap the share icon and then "Add to Home Screen"'
          place='bottom'
        />
      </div>
    )
  }

  // Android / Desktop - Show if visible
  if (!isVisible) return null

  // Get tooltip content based on whether we have a prompt
  const getTooltipContent = () => {
    if (deferredPrompt) {
      return 'Click to install app'
    }
    const instructions = getInstallInstructions()
    return `Install app - ${instructions}`
  }

  return (
    <button
      onClick={handleInstallClick}
      className='cursor-pointer flex items-center justify-center transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded'
      aria-label='Install app'
    >
      <span
        data-tooltip-id='installPwaTooltip'
        data-tooltip-content={getTooltipContent()}
        className='items-center relative block'
      >
        <Download
          className='h-auto w-full max-w-[20px] md:max-w-[24px] block'
          aria-hidden='true'
        />
      </span>
      <Tooltip
        id='installPwaTooltip'
        content={getTooltipContent()}
        place='bottom'
      />
    </button>
  )
}
